import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Package, Pencil, Plus, Receipt, Shield, Tag, Trash2, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isOnSale } from "@/lib/pricing";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORY_OPTIONS = [
  { value: "ranks", label: "Ranks" },
  { value: "keys", label: "Crate Keys" },
  { value: "ulti", label: "ULTI+" },
  { value: "donate", label: "Donate" },
];

const EMPTY_FORM = { name: "", category: "ranks", price: "", sale_price: "", sale_ends_at: "", tebex_id: "", description: "", image_url: "", perks: "", featured: false, deal_roles: [], deal_users: [], recurring: null };

const toLocalInput = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", percent: "", expires_at: "" });
  const [users, setUsers] = useState([]);
  const [goalTarget, setGoalTarget] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const [flashForm, setFlashForm] = useState({ active: false, ends_at: "", title: "", coupon_code: "" });
  const [savingFlash, setSavingFlash] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [tebexList, setTebexList] = useState(null);
  const [importing, setImporting] = useState(false);

  const openImport = async () => {
    setImportOpen(true);
    setTebexList(null);
    try {
      const { data } = await api.get("/admin/tebex/packages");
      setTebexList(data);
    } catch (e) {
      setTebexList([]);
      toast.error("Could not load Tebex packages", { description: formatApiError(e) });
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/tebex/import");
      toast.success("Import complete", { description: `${data.imported} new, ${data.updated} updated` });
      setImportOpen(false);
      await load();
    } catch (e) {
      toast.error("Import failed", { description: formatApiError(e) });
    } finally {
      setImporting(false);
    }
  };

  const load = useCallback(async () => {
    const [s, p, o, c, g, f, u] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/packages"),
      api.get("/admin/orders"),
      api.get("/admin/coupons"),
      api.get("/goal"),
      api.get("/flash-sale"),
      api.get("/admin/users"),
    ]);
    setStats(s.data);
    setPackages(p.data);
    setOrders(o.data);
    setCoupons(c.data);
    setGoalTarget(String(g.data.target));
    setUsers(u.data);
    setFlashForm({
      active: !!f.data.active,
      ends_at: f.data.ends_at ? toLocalInput(f.data.ends_at) : "",
      title: f.data.title || "",
      coupon_code: f.data.coupon_code || "",
    });
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(() => load().finally(() => setReady(true)))
      .catch(() => {
        localStorage.removeItem("ulti_admin_token");
        navigate("/admin/login");
      });
  }, [navigate, load]);

  const openCreate = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };

  const openEdit = (pkg) => {
    setEditing(pkg.id);
    setForm({
      name: pkg.name,
      category: pkg.category,
      price: String(pkg.price),
      sale_price: pkg.sale_price != null ? String(pkg.sale_price) : "",
      sale_ends_at: pkg.sale_ends_at ? toLocalInput(pkg.sale_ends_at) : "",
      tebex_id: pkg.tebex_id != null ? String(pkg.tebex_id) : "",
      description: pkg.description,
      image_url: pkg.image_url,
      perks: (pkg.perks || []).join("\n"),
      featured: !!pkg.featured,
      deal_roles: pkg.deal_roles || [],
      deal_users: pkg.deal_users || [],
      recurring: pkg.recurring || null,
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      price: parseFloat(form.price),
      sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
      sale_ends_at: form.sale_ends_at ? new Date(form.sale_ends_at).toISOString() : null,
      tebex_id: form.tebex_id ? parseInt(form.tebex_id, 10) : null,
      description: form.description.trim(),
      image_url: form.image_url.trim(),
      perks: form.perks.split("\n").map((p) => p.trim()).filter(Boolean),
      featured: form.featured,
      deal_roles: form.deal_roles,
      deal_users: form.deal_users,
      recurring: form.recurring || null,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/packages", payload);
        toast.success("Package created");
      } else {
        await api.put(`/admin/packages/${editing}`, payload);
        toast.success("Package updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/packages/${deleting}`);
      toast.success("Package deleted");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim() || !couponForm.percent) {
      toast.error("Code and percent are required");
      return;
    }
    try {
      await api.post("/admin/coupons", {
        code: couponForm.code.trim(),
        percent: parseFloat(couponForm.percent),
        expires_at: couponForm.expires_at ? new Date(couponForm.expires_at).toISOString() : null,
      });
      toast.success("Coupon created");
      setCouponForm({ code: "", percent: "", expires_at: "" });
      await load();
    } catch (e) {
      toast.error("Create failed", { description: formatApiError(e) });
    }
  };

  const saveGoal = async () => {
    const target = parseFloat(goalTarget);
    if (!target || target <= 0) {
      toast.error("Enter a goal amount greater than zero");
      return;
    }
    setSavingGoal(true);
    try {
      await api.put("/admin/goal", { target });
      toast.success("Goal updated");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSavingGoal(false);
    }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await api.put(`/admin/coupons/${coupon.id}`, { active: !coupon.active });
      await load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e) });
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success("Coupon deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    }
  };

  const saveFlash = async () => {
    if (flashForm.active && !flashForm.ends_at) {
      toast.error("Set an end date for the flash sale");
      return;
    }
    setSavingFlash(true);
    try {
      await api.put("/admin/flash-sale", {
        active: flashForm.active,
        ends_at: flashForm.ends_at ? new Date(flashForm.ends_at).toISOString() : null,
        title: flashForm.title.trim(),
        coupon_code: flashForm.coupon_code.trim(),
      });
      toast.success(flashForm.active ? "Flash sale is live" : "Flash sale turned off");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setSavingFlash(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ulti_admin_token");
    navigate("/admin/login");
  };

  if (!ready) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#8B5CF6]" data-testid="admin-loading" />
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen">
      <header className="sticky top-0 z-40 bg-[#0D0B12]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#8B5CF6]" strokeWidth={2.5} />
            <span className="font-display text-lg tracking-tight text-white">ULTI<span className="text-[#A78BFA]">SMP</span></span>
            <span className="ml-3 text-[10px] font-bold tracking-[0.25em] text-slate-500 border border-white/10 rounded-full px-3 py-1">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} data-testid="admin-view-store-button" className="px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-300">
              VIEW STORE
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-300">
              <LogOut className="w-4 h-4" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { label: "REVENUE", value: `$${stats.revenue.toFixed(2)}`, testid: "admin-stat-revenue" },
              { label: "ORDERS", value: stats.orders, testid: "admin-stat-orders" },
              { label: "PACKAGES", value: stats.packages, testid: "admin-stat-packages" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-6" data-testid={s.testid}>
                <p className="text-[10px] font-bold tracking-[0.25em] text-slate-500">{s.label}</p>
                <p className="mt-3 font-display text-4xl tracking-tight text-white">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-6 flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="space-y-2 flex-1">
            <Label className="text-slate-400 text-xs tracking-[0.15em]">MONTHLY DONATION GOAL (USD)</Label>
            <Input
              type="number" min="1" step="1"
              value={goalTarget}
              onChange={(e) => setGoalTarget(e.target.value)}
              data-testid="goal-form-target"
              className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]"
            />
          </div>
          <button
            onClick={saveGoal}
            disabled={savingGoal}
            data-testid="goal-form-save-button"
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-xs font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300"
          >
            {savingGoal && <Loader2 className="w-4 h-4 animate-spin" />}
            SAVE GOAL
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="space-y-2 flex-1">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">FLASH SALE TITLE</Label>
              <Input
                value={flashForm.title}
                onChange={(e) => setFlashForm({ ...flashForm, title: e.target.value })}
                placeholder="FLASH SALE"
                data-testid="flash-form-title"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
              />
            </div>
            <div className="space-y-2 w-full sm:w-44">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">COUPON CODE (OPTIONAL)</Label>
              <Input
                value={flashForm.coupon_code}
                onChange={(e) => setFlashForm({ ...flashForm, coupon_code: e.target.value.toUpperCase() })}
                placeholder="e.g. ULTISMP"
                data-testid="flash-form-coupon"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
              />
            </div>
            <div className="space-y-2 w-full sm:w-64">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">ENDS AT</Label>
              <Input
                type="datetime-local"
                value={flashForm.ends_at}
                onChange={(e) => setFlashForm({ ...flashForm, ends_at: e.target.value })}
                data-testid="flash-form-ends"
                style={{ colorScheme: "dark" }}
                className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]"
              />
            </div>
            <div className="flex items-center gap-4 pb-1">
              <div className="flex items-center gap-2">
                <Switch checked={flashForm.active} onCheckedChange={(v) => setFlashForm({ ...flashForm, active: v })} data-testid="flash-form-toggle" />
                <span className="text-sm text-slate-300">{flashForm.active ? "Live" : "Off"}</span>
              </div>
              <button
                onClick={saveFlash}
                disabled={savingFlash}
                data-testid="flash-form-save-button"
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-black text-xs font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300"
              >
                {savingFlash && <Loader2 className="w-4 h-4 animate-spin" />}
                SAVE FLASH SALE
              </button>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">Shows every package currently on sale at /flash-sale with a live countdown. The navbar FLASH link pulses while it's live.</p>
        </div>

        <Tabs defaultValue="packages" className="mt-10">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="packages" data-testid="admin-tab-packages" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" /> Packages
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">
              <Receipt className="w-4 h-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="coupons" data-testid="admin-tab-coupons" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">
              <Tag className="w-4 h-4 mr-2" /> Coupons
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Users
              {users.filter((u) => !u.approved).length > 0 && (
                <span data-testid="users-pending-badge" className="ml-2 min-w-5 h-5 px-1 rounded-full bg-amber-400 text-black text-[10px] font-extrabold flex items-center justify-center">
                  {users.filter((u) => !u.approved).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="mt-6">
            <div className="flex justify-end gap-3 mb-4">
              <button
                onClick={openImport}
                data-testid="admin-import-tebex-button"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#8B5CF6]/60 text-slate-300 hover:text-white text-xs font-bold tracking-[0.15em] transition-[background-color,border-color,color] duration-300"
              >
                IMPORT FROM TEBEX
              </button>
              <button
                onClick={openCreate}
                data-testid="admin-add-package-button"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-bold tracking-[0.15em] transition-[background-color] duration-300"
              >
                <Plus className="w-4 h-4" /> ADD PACKAGE
              </button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500">Package</TableHead>
                    <TableHead className="text-slate-500">Category</TableHead>
                    <TableHead className="text-slate-500">Price</TableHead>
                    <TableHead className="text-slate-500">Featured</TableHead>
                    <TableHead className="text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-package-row-${pkg.id}`}>
                      <TableCell className="text-white font-medium">{pkg.name}</TableCell>
                      <TableCell className="text-slate-400 capitalize">{pkg.category}</TableCell>
                      <TableCell className="font-mono">
                        {isOnSale(pkg) ? (
                          <span>
                            <span className="line-through text-slate-500 text-xs mr-2">${pkg.price.toFixed(2)}</span>
                            <span className="text-amber-300">${pkg.sale_price.toFixed(2)}</span>
                          </span>
                        ) : (
                          <span className="text-[#A78BFA]">${pkg.price.toFixed(2)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">{pkg.featured ? "Yes" : "—"}</TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => openEdit(pkg)} data-testid={`admin-edit-${pkg.id}`} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors duration-200" aria-label={`Edit ${pkg.name}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleting(pkg.id)} data-testid={`admin-delete-${pkg.id}`} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200" aria-label={`Delete ${pkg.name}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500">Order</TableHead>
                    <TableHead className="text-slate-500">Player</TableHead>
                    <TableHead className="text-slate-500">Items</TableHead>
                    <TableHead className="text-slate-500">Total</TableHead>
                    <TableHead className="text-slate-500">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-order-row-${o.id}`}>
                      <TableCell className="font-mono text-slate-400">{o.id.slice(0, 8).toUpperCase()}</TableCell>
                      <TableCell className="text-white font-medium">
                        {o.username}
                        {o.is_gift && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 border border-[#8B5CF6]/40 text-[#C4B5FD] text-[10px] font-bold tracking-wide">
                            GIFT{o.buyer_username ? ` FROM ${o.buyer_username.toUpperCase()}` : ""}
                          </span>
                        )}
                        {o.is_gift && o.gift_message && (
                          <p className="mt-1 text-xs text-slate-500 italic font-normal">"{o.gift_message}"</p>
                        )}
                        {o.coupon_code && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold tracking-wide">
                            {o.coupon_code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">{o.items.map((i) => `${i.name} x${i.qty}`).join(", ")}</TableCell>
                      <TableCell className="text-[#A78BFA] font-mono">${o.total.toFixed(2)}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="coupons" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 p-6 mb-6 flex flex-col sm:flex-row gap-4 sm:items-end">
              <div className="space-y-2 flex-1">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">CODE</Label>
                <Input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SUMMER20"
                  data-testid="coupon-form-code"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6] uppercase"
                />
              </div>
              <div className="space-y-2 w-full sm:w-40">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">PERCENT OFF</Label>
                <Input
                  type="number" min="1" max="100"
                  value={couponForm.percent}
                  onChange={(e) => setCouponForm({ ...couponForm, percent: e.target.value })}
                  placeholder="20"
                  data-testid="coupon-form-percent"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
                />
              </div>
              <div className="space-y-2 w-full sm:w-56">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">EXPIRES (OPTIONAL)</Label>
                <Input
                  type="datetime-local"
                  value={couponForm.expires_at}
                  onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
                  data-testid="coupon-form-expires"
                  style={{ colorScheme: "dark" }}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]"
                />
              </div>
              <button
                onClick={createCoupon}
                data-testid="coupon-form-create-button"
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-bold tracking-[0.15em] transition-[background-color] duration-300"
              >
                <Plus className="w-4 h-4" /> CREATE COUPON
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500">Code</TableHead>
                    <TableHead className="text-slate-500">Discount</TableHead>
                    <TableHead className="text-slate-500">Expires</TableHead>
                    <TableHead className="text-slate-500">Active</TableHead>
                    <TableHead className="text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                      {coupons.length === 0 ? (
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableCell colSpan={5} className="text-center text-slate-500 py-10">No coupons yet — create one above.</TableCell>
                    </TableRow>
                  ) : coupons.map((c) => (
                    <TableRow key={c.id} className="border-white/10 hover:bg-white/5" data-testid={`coupon-row-${c.code}`}>
                      <TableCell className="text-white font-mono font-semibold">{c.code}</TableCell>
                      <TableCell className="text-emerald-400 font-mono">{c.percent}%</TableCell>
                      <TableCell className="text-slate-400 text-xs">
                        {c.expires_at ? new Date(c.expires_at).toLocaleString() : "Never"}
                      </TableCell>
                      <TableCell>
                        <Switch checked={c.active} onCheckedChange={() => toggleCoupon(c)} data-testid={`coupon-toggle-${c.code}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => deleteCoupon(c.id)} data-testid={`coupon-delete-${c.code}`} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200" aria-label={`Delete ${c.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500">Email</TableHead>
                    <TableHead className="text-slate-500">IGN</TableHead>
                    <TableHead className="text-slate-500">Status</TableHead>
                    <TableHead className="text-slate-500">Role</TableHead>
                    <TableHead className="text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableCell colSpan={5} className="text-center text-slate-500 py-10">No accounts yet — players register from the storefront.</TableCell>
                    </TableRow>
                  ) : users.map((u) => (
                    <TableRow key={u.id} className="border-white/10 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-white">{u.email || "—"}</TableCell>
                      <TableCell className="text-slate-300 font-medium">{u.ign || "—"}</TableCell>
                      <TableCell>
                        {u.approved ? (
                          <span className="px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] bg-white/5 text-slate-400" data-testid={`user-status-${u.id}`}>APPROVED</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] bg-amber-400/20 text-amber-300" data-testid={`user-status-${u.id}`}>PENDING</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={u.role}
                          onValueChange={async (role) => {
                            try {
                              await api.patch(`/admin/users/${u.id}`, { role });
                              toast.success(`${u.ign || u.email} is now ${role}`);
                              await load();
                            } catch (e) {
                              toast.error("Role update failed", { description: formatApiError(e) });
                            }
                          }}
                        >
                          <SelectTrigger data-testid={`user-role-select-${u.id}`} className="w-32 bg-white/5 border-white/10 text-white h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0D0B12] border-white/10 text-white">
                            <SelectItem value="member">member</SelectItem>
                            <SelectItem value="staff">staff</SelectItem>
                            <SelectItem value="media">media</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!u.approved && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.patch(`/admin/users/${u.id}`, { approved: true });
                                  toast.success(`${u.ign || u.email} approved`);
                                  await load();
                                } catch (e) {
                                  toast.error("Approve failed", { description: formatApiError(e) });
                                }
                              }}
                              data-testid={`user-approve-${u.id}`}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold tracking-[0.15em] bg-emerald-400/20 text-emerald-300 hover:bg-emerald-400 hover:text-black transition-colors duration-200"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> APPROVE
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              try {
                                await api.delete(`/admin/users/${u.id}`);
                                toast.success("Account deleted");
                                await load();
                              } catch (e) {
                                toast.error("Delete failed", { description: formatApiError(e) });
                              }
                            }}
                            data-testid={`user-delete-${u.id}`}
                            className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200"
                            aria-label={`Delete ${u.email || u.ign}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[#0D0B12] border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="package-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing === "new" ? "New package" : "Edit package"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">NAME</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-form-name" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">PRICE (USD)</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="package-form-price" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">SALE PRICE (OPTIONAL)</Label>
                <Input type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="package-form-sale-price" placeholder="No sale" className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs tracking-[0.15em]">SALE ENDS (OPTIONAL)</Label>
                <Input type="datetime-local" value={form.sale_ends_at} onChange={(e) => setForm({ ...form, sale_ends_at: e.target.value })} data-testid="package-form-sale-ends" style={{ colorScheme: "dark" }} className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">CATEGORY</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="package-form-category" className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0D0B12] border-white/10 text-white">
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">TEBEX PACKAGE ID (OPTIONAL — ENABLES REAL CHECKOUT)</Label>
              <Input type="number" min="1" value={form.tebex_id} onChange={(e) => setForm({ ...form, tebex_id: e.target.value })} data-testid="package-form-tebex-id" placeholder="From your Tebex panel" className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">BILLING</Label>
              <button
                type="button"
                onClick={() => setForm({ ...form, recurring: form.recurring ? null : "month" })}
                data-testid="package-form-recurring"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold tracking-[0.15em] border transition-[background-color,border-color,color] duration-300 ${
                  form.recurring
                    ? "bg-[#8B5CF6]/20 border-[#8B5CF6]/50 text-[#A78BFA]"
                    : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                }`}
              >
                {form.recurring ? "MONTHLY SUBSCRIPTION" : "ONE-TIME PURCHASE"}
              </button>
              <p className="text-[11px] text-slate-600">Monthly = price shows as /mo with a cancel-anytime note. Match this to the package's billing type in Tebex.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">DESCRIPTION</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="package-form-description" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" rows={2} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">IMAGE URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="package-form-image" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">PERKS (ONE PER LINE)</Label>
              <Textarea value={form.perks} onChange={(e) => setForm({ ...form, perks: e.target.value })} data-testid="package-form-perks" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]" rows={4} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs tracking-[0.15em]">PRIVATE DEAL (OPTIONAL)</Label>
              <div className="flex gap-2">
                {["staff", "media"].map((role) => {
                  const on = form.deal_roles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setForm({ ...form, deal_roles: on ? form.deal_roles.filter((r) => r !== role) : [...form.deal_roles, role] })}
                      data-testid={`package-form-deal-${role}`}
                      className={`px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] border transition-[background-color,border-color,color] duration-300 ${
                        on
                          ? role === "staff"
                            ? "bg-emerald-400 border-emerald-400 text-black"
                            : "bg-amber-400 border-amber-400 text-black"
                          : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                      }`}
                    >
                      {role.toUpperCase()} ONLY
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-600">Selected roles = hidden from everyone except logged-in accounts with that role.</p>
              {users.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {users.map((u) => {
                      const on = form.deal_users.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setForm({ ...form, deal_users: on ? form.deal_users.filter((id) => id !== u.id) : [...form.deal_users, u.id] })}
                          data-testid={`package-form-deal-user-${u.id}`}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-wide border transition-[background-color,border-color,color] duration-300 ${
                            on
                              ? "bg-[#8B5CF6] border-[#8B5CF6] text-white"
                              : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                          }`}
                        >
                          {u.ign || u.email}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-600">Selected accounts = only those specific players can see and buy this package.</p>
                </>
              )}
              <p className="text-[11px] text-amber-400/70">Tip: for a discounted deal, the buyer pays the linked Tebex package's price — create the cheaper version in your Tebex panel and paste its ID above.</p>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <Label className="text-slate-300 text-sm">Featured on homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="package-form-featured" />
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="package-form-save-button"
              className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              SAVE PACKAGE
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#0D0B12] border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tebex-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Import from Tebex</DialogTitle>
          </DialogHeader>
          {tebexList === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" /></div>
          ) : tebexList.length === 0 ? (
            <p className="py-8 text-center text-slate-500 text-sm" data-testid="tebex-import-empty">No packages found on your Tebex store.</p>
          ) : (
            <div className="space-y-2" data-testid="tebex-import-list">
              {tebexList.map((p) => (
                <div key={p.tebex_id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid={`tebex-import-row-${p.tebex_id}`}>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-white/5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-500">{p.tebex_category} → <span className="text-[#A78BFA] capitalize">{p.category}</span></p>
                  </div>
                  <span className="font-mono text-sm text-[#A78BFA]">${Number(p.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={runImport}
            disabled={importing || !tebexList || tebexList.length === 0}
            data-testid="tebex-import-confirm-button"
            className="mt-4 w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
          >
            {importing && <Loader2 className="w-4 h-4 animate-spin" />}
            IMPORT ALL PACKAGES
          </button>
          <p className="text-[11px] text-slate-600 text-center">Existing linked packages are updated in place — your perks, sales and featured flags are kept.</p>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>        <AlertDialogContent className="bg-[#0D0B12] border-white/10 text-white" data-testid="package-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this package?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This cannot be undone. Players will no longer see it in the store.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="package-delete-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="package-delete-confirm" className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
